import {DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useMutation} from '@github-ui/react-query'
import {applyPendingIssueSuggestionsMutation as queryId} from '@github-ui/server-persisted-queries'
import type {ApplyPendingIssueSuggestionsMutationMutation} from '@github-ui/server-persisted-queries/types/applyPendingIssueSuggestionsMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {
  type PendingSuggestionMutationOptions,
  pendingSuggestionOperation,
  type SuggestionRef,
  toSuggestionInput,
} from '../utils/suggestion-ref'
import {issueLifecyclePath} from './issue-lifecycle-mutation'

export type ApplySuggestionRef = SuggestionRef

type ApplyPendingIssueSuggestionsResult = {
  data: ApplyPendingIssueSuggestionsMutationMutation
  errors: ReadonlyArray<{message?: string}>
}
type ApplyPendingIssueSuggestionsVariables = {
  operation: ReturnType<typeof pendingSuggestionOperation>
  actorId: string
  suggestions: SuggestionRef[]
  onApplied?: () => void
  onRollback?: () => void
}

export function useApplyPendingIssueSuggestions({
  issueId,
  owner,
  repository,
  issueNumber,
  onCompleted,
  onError,
}: PendingSuggestionMutationOptions<ApplyPendingIssueSuggestionsMutationMutation>) {
  const {mutate} = useMutation<ApplyPendingIssueSuggestionsResult, Error, ApplyPendingIssueSuggestionsVariables>({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: async ({actorId, suggestions}) => {
      const path = `${issueLifecyclePath(owner, repository, issueNumber)}/pending_suggestions.json`
      const response = await reactFetchJSON(`${path}?${new URLSearchParams({queryId})}`, {
        method: 'PUT',
        body: {actorId, suggestions: suggestions.map(toSuggestionInput)},
      })
      if (!response.ok) throw new Error(`Could not accept suggestions (${response.status})`)
      const json = (await response.json()) as {
        data?: ApplyPendingIssueSuggestionsMutationMutation
        errors?: Array<{message?: string}>
      }
      // Relay completed responses containing data even with GraphQL errors.
      // In particular, apply can return confirmed assignments after a partial failure.
      if (!json.data) {
        throw new Error(json.errors?.[0]?.message ?? 'Could not accept suggestions')
      }
      return {data: json.data, errors: json.errors ?? []}
    },
    onMutate: ({onApplied}) => onApplied?.(),
    onSuccess: async ({data, errors}, {operation, onRollback}) => {
      const issue = data.applyPendingIssueSuggestions?.issue
      const error = errors.find(item => item.message)?.message
      const hasErrors = errors.length > 0

      if (issue) {
        await notifySettlement(
          {
            type: 'setFields',
            id: issue.id,
            fields: {assignedActors: issue.assignedActors, agentAssignments: issue.agentAssignments},
          },
          ENTITY_FIELDS_OWNER,
          DOCUMENT_FIELDS_PATH,
        )
      }

      if (!hasErrors && issue) {
        await notifySettlement(operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH)
      }

      if (hasErrors || !issue) {
        onRollback?.()
        onError?.(new Error(error ?? 'Could not accept all suggestions'))
        return
      }
      onCompleted?.(data)
    },
    onError: (error, {onRollback}) => {
      onRollback?.()
      onError?.(error)
    },
  })

  return useCallback(
    (
      actorId: string,
      suggestions: SuggestionRef[],
      onApplied?: () => void,
      additionalSuggestionIds?: string[],
      onRollback?: () => void,
    ) => {
      const operation = pendingSuggestionOperation(issueId, {
        suggestionIds: [...suggestions.map(suggestion => suggestion.id), ...(additionalSuggestionIds ?? [])],
        state: 'APPLIED',
      })
      mutate({operation, actorId, suggestions, onApplied, onRollback})
    },
    [issueId, mutate],
  )
}
